import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { Router } from '@angular/router';
import { ConnectionService } from '@services/connection-service';

export interface PaidPastQuery {
  objectId?: string;
  status?: string;
  category?: string;
  llmResponse?: { category?: string };
  supportQuestion?: { questionLanguageString?: Record<string, string> };
  languagePreference?: string;
  createdAt: Date;
  title?: string;
}

interface PaidPastQueriesSheetData {
  queries: PaidPastQuery[];
}

@Component({
  selector: 'app-paid-past-queries-sheet',
  templateUrl: './paid-past-queries-sheet.component.html',
  styles: [`
    .paid-past-query-sheet {
      width: 100%;
      background: #f2f3f6 !important;
      will-change: height, border-radius;
    }

    .paid-past-query-sheet > header h2 { line-height: 26px; }

    .paid-past-query-section-title {
      margin: 0 0 12px 17px;
      color: #222;
      font-family: 'Lexend', sans-serif;
      font-size: 14px;
      font-weight: 500;
      line-height: 26px;
    }

    .paid-past-query-sheet:not(.paid-past-query-sheet-full-page) {
      border-radius: 24px 24px 0 0 !important;
    }

    .paid-past-query-sheet:not(.paid-past-query-sheet-full-page) > header {
      position: relative;
      padding: 32px 16px 8px !important;
      gap: 8px;
    }

    .paid-past-query-sheet:not(.paid-past-query-sheet-full-page) > header > button {
      position: absolute;
      top: 12px;
      right: 18px;
      width: 24px !important;
      height: 24px !important;
      color: #616161 !important;
    }

    .paid-past-query-sheet:not(.paid-past-query-sheet-full-page) .paid-past-query-close-icon {
      opacity: 0.62;
    }

    .paid-past-query-sheet:not(.paid-past-query-sheet-full-page) > div {
      padding-right: 7px !important;
      padding-left: 7px !important;
    }

    .paid-past-query-sheet-full-page {
      height: 100dvh !important;
      background: #f3f3f3 !important;
    }

    .paid-past-query-sheet-full-page > header {
      position: relative;
      min-height: 179.7px;
      padding: 145.6px 24px 8px !important;
      box-sizing: border-box;
    }

    .paid-past-query-sheet-full-page > header > button {
      position: absolute;
      top: 58px;
      left: 24px;
      width: 40px !important;
      height: 40px !important;
      border-radius: 12px !important;
      background: #222 !important;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .paid-past-query-sheet-full-page > div {
      padding-right: 7px !important;
      padding-left: 7px !important;
      overscroll-behavior: contain;
      height: calc(100dvh - 180px);
      flex: 1 1 auto;
      overflow-y: scroll !important;
    }

    .paid-past-query-sheet-settling {
      transition: height 440ms cubic-bezier(0.22, 1, 0.36, 1), border-radius 440ms cubic-bezier(0.22, 1, 0.36, 1);
    }

    .paid-past-query-sheet-full-page > header > button img {
      filter: brightness(0) invert(1);
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
})
export class PaidPastQueriesSheetComponent {
  // The Figma popup occupies the lower 55% of the viewport before it expands.
  private static readonly COLLAPSED_HEIGHT_DVH = 55;
  private static readonly EXPANSION_HEIGHT_DVH = 45;

  private readonly data = inject<PaidPastQueriesSheetData | null>(MAT_BOTTOM_SHEET_DATA, { optional: true });
  private readonly bottomSheetRef = inject<MatBottomSheetRef<PaidPastQueriesSheetComponent> | null>(MatBottomSheetRef, { optional: true });
  private readonly router = inject(Router);
  private readonly connectionService = inject(ConnectionService);
  private lastTouchY: number | null = null;

  readonly routeMode = input(false);
  readonly suppliedQueries = input<PaidPastQuery[] | null>(null, { alias: 'queries' });
  readonly queries = computed((): PaidPastQuery[] => this.suppliedQueries() ?? this.data?.queries ?? []);
  readonly recentQueries = computed((): PaidPastQuery[] => this.queries().filter((query: PaidPastQuery): boolean => this.isOpenQuery(query)));
  readonly pastQueries = computed((): PaidPastQuery[] => this.queries().filter((query: PaidPastQuery): boolean => !this.isOpenQuery(query)));
  readonly expansionProgress = signal(0);
  readonly isSettling = signal(false);
  readonly isFullPage = computed((): boolean => this.routeMode() || this.expansionProgress() >= 1);
  readonly sheetHeight = computed((): string => {
    if (this.routeMode()) return '100dvh';
    const height = PaidPastQueriesSheetComponent.COLLAPSED_HEIGHT_DVH
      + (PaidPastQueriesSheetComponent.EXPANSION_HEIGHT_DVH * this.expansionProgress());
    return `${height}dvh`;
  });

  isOpenQuery(query: PaidPastQuery): boolean {
    return query?.status !== 'Completed';
  }

  getQueryCategory(query: PaidPastQuery): string {
    const category = query?.llmResponse?.category;
    return category && category !== 'default' ? category : query?.category || 'Query';
  }

  getQueryTitle(query: PaidPastQuery): string {
    return query?.title
      || query?.supportQuestion?.questionLanguageString?.[query?.languagePreference || '']
      || 'Query';
  }

  close(): void {
    if (this.routeMode()) {
      void this.router.navigate(['/support']);
      return;
    }
    this.bottomSheetRef?.dismiss();
  }

  async openQuery(query: PaidPastQuery): Promise<void> {
    if (!query?.objectId) return;

    const username = this.connectionService.getActingUser()?.get('username');
    this.bottomSheetRef?.dismiss();
    await this.router.navigate([`/support/ticket/${query.objectId}`], { queryParams: { username } });
  }

  onWheel(event: WheelEvent, contentScrollTop: number): void {
    if (this.isFullPage()) return;
    if (event.deltaY <= 0 || contentScrollTop > 0) return;
    event.preventDefault();
    this.settleExpansion(1);
  }

  onTouchStart(event: TouchEvent): void {
    this.lastTouchY = event.touches[0]?.clientY ?? null;
  }

  onTouchMove(event: TouchEvent, contentScrollTop: number): void {
    if (this.isFullPage()) return;
    const currentY = event.touches[0]?.clientY;
    if (currentY === undefined || this.lastTouchY === null) return;

    const deltaY = this.lastTouchY - currentY;
    this.lastTouchY = currentY;
    if (deltaY > 0 && contentScrollTop <= 0) {
      event.preventDefault();
      this.settleExpansion(1);
    }
  }

  onTouchEnd(): void {
    this.lastTouchY = null;
    if (!this.isFullPage() && this.expansionProgress() > 0) this.settleExpansion(1);
  }

  onPointerDown(event: PointerEvent): void {
    if (this.isFullPage()) return;
    this.lastTouchY = event.clientY;
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
  }

  onPointerMove(event: PointerEvent, contentScrollTop: number): void {
    if (this.isFullPage() || this.lastTouchY === null) return;
    const deltaY = this.lastTouchY - event.clientY;
    this.lastTouchY = event.clientY;
    if (deltaY > 0 && contentScrollTop <= 0) {
      event.preventDefault();
      this.settleExpansion(1);
    }
  }

  onPointerEnd(): void {
    this.lastTouchY = null;
  }

  onTransitionEnd(event: TransitionEvent): void {
    if (event.propertyName !== 'height') return;
    this.isSettling.set(false);
  }

  private settleExpansion(target: 0 | 1): void {
    if (this.isSettling() || this.expansionProgress() === target) return;
    this.isSettling.set(true);
    window.requestAnimationFrame((): void => {
      this.expansionProgress.set(target);
    });
  }

}
